// TestRequirementReport.tsx
import {
  Alert,
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Checkbox as MUICheckbox,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  type DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBoundStore } from "Zustand/store/mainStore";
import { v4 as uuidv4 } from "uuid";
import { Controller, useForm } from "react-hook-form";
import { Button, Checkbox, TextField, Typography } from "Atoms";
import AttachmentManager from "Atoms/AttachmentManager";
import {
  IMAGE_EXTS,
  getAttachmentChanges,
} from "Utils/helperFunctions";
import { deleteAttachmentsAPI } from "Zustand/services/attachmentService";
import moment, { Moment } from "moment";
import {
  getTdmsLogParametersAPI,
  updateTestRequirementReportAPI,
} from "Zustand/services/reportServices";
import {
  FileDirectoryItem,
  deleteFilesAPI,
  getFileDirectoryFilesAPI,
  requestFileDirectoryUploadUrlsAPI,
  uploadFilesToFileDirectory,
} from "Zustand/services/fileDirectoryService";
import {
  DescriptionOutlined,
  ExpandLess,
  ExpandMore,
  FolderOutlined,
  ImageOutlined,
  InsertDriveFileOutlined,
  MovieOutlined,
  PictureAsPdf,
  TableChartOutlined,
  DeleteOutline,
  // Download,
  UploadFile,
} from "@mui/icons-material";
import { toast } from "react-toastify";

interface Attachment {
  id: string;
  title: string;
  description: string;
  file?: File;
  url?: string;
  nodeid?: string;
  mimetype?: string;
}

interface RequirementReportData {
  nodeid: string;
  name: string;
  startdate?: number | string | Moment;
  enddate?: number | string | Moment;
  usedequipment?: string;
  responsibletestengineer?: string;
  deviationfromthemethod?: string;
  chamberlogsample?: string;
  functionaltestresult?: string;
  observations?: string;
  hasattachment?: Attachment[];
  filedirectoryid?: string;
  testmeasurementfiles?: {
    filedirectoryid?: string;
  };
  logtype?: string;
  availablelogparameters?: string[];
  selectedlogparameters?: string[];
  testaccordingtoaccreditation?: boolean;
  testrequirementreport?: any;
}

interface RequirementProps {
  requirement: RequirementReportData;
  selectedId: string | null;
  resetTree: any;
  setSelectedId: any;
  reportnodeid: string;
  viewMode?: "results" | "files";
}

const isImageAttachment = (attachment: Attachment) => {
  if (attachment.mimetype) return attachment.mimetype.startsWith("image/");
  if (attachment.file) return attachment.file.type.startsWith("image/");
  if (attachment.url) return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(attachment.url);
  return false;
};

const getMeasurementFileDirectoryId = (reportData?: RequirementReportData | null) => {
  // Measurement files now come from the dedicated KH filedirectory relation on
  // testmeasurementfiles. Keep the older shapes as fallbacks for existing data.
  return (
    reportData?.testmeasurementfiles?.filedirectoryid ??
    reportData?.filedirectoryid ??
    (reportData as any)?.filedirectory?.filedirectoryid ??
    (reportData as any)?.filedirectory?.id ??
    ""
  );
};

type FileDirectoryManagerProps = {
  objectNodeId?: string;
  filedirectoryid?: string;
  files: FileDirectoryItem[];
  loading: boolean;
  uploading: boolean;
  onUpload: (files: File[]) => Promise<void>;
  onDelete: (files: FileDirectoryItem[]) => Promise<void>;
};

type FileDirectoryTreeNode = {
  id: string;
  name: string;
  type: "folder" | "file";
  children: FileDirectoryTreeNode[];
  file?: FileDirectoryItem;
};

type FileDirectoryDeleteTarget = {
  type: "file" | "folder";
  name: string;
  files: FileDirectoryItem[];
};

type UploadableFile = File & {
  relativePath?: string;
};

const LOG_TYPE_SPEAKER = "Speaker logs";
const LOG_TYPE_TDMS = "TDMS logs";
const LOG_TYPE_OPTIONS = [LOG_TYPE_SPEAKER, LOG_TYPE_TDMS];
const SPEAKER_LOG_PARAMETERS = [
  "Vrms(V)",
  "Irms(A)",
  "Zreal(Ohms)",
  "Zimag(Ohms)",
  "Zmag(Ohms)",
  "ZPhase(degrees)",
  "VCT(°C)",
  "Power(W)",
  "Temperature(°C)",
  "Humidity(%RH)",
  "Resonance Freq(Hz)",
  "Res(Ohms)",
  "Crest Factor",
  "Thermal Coefficient",
  "Thermocouple (°C)",
  "Frequency (Hz)",
  "Z (Magnitude)",
  "Z (Magnitude - Optimised)",
];
const DEFAULT_SPEAKER_LOG_PARAMETERS = [
  "Vrms(V)",
  "Irms(A)",
  "Zreal(Ohms)",
  "VCT(°C)",
  "Resonance Freq(Hz)",
];

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        const option = item as Record<string, unknown>;
        return (
          option.displayname ??
          option.displayName ??
          option.name ??
          option.value ??
          option.key ??
          option.parameter ??
          ""
        );
      }
      return "";
    })
    .map(String)
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeTdmsParameters = (response: unknown): string[] => {
  const responseObject =
    response && typeof response === "object"
      ? (response as Record<string, unknown>)
      : {};
  const candidates =
    Array.isArray(response)
      ? response
      : responseObject.availablelogparameters ??
        responseObject.selectedlogparameters ??
        responseObject.logparameters ??
        responseObject.parameters ??
        responseObject.data ??
        [];

  return Array.from(new Set(toStringArray(candidates)));
};

const detectLogTypeFromFiles = (files: FileDirectoryItem[]) => {
  // Measurement file extensions are our first clue before the user explicitly
  // saves log settings. Speaker logs win when mixed with TDMS because DAT/SPKD
  // files can sit beside TDMS exports, and the caller already preserves any
  // saved/manual log type before this fallback runs.
  const filenames = files.map((file) =>
    `${file.filename ?? ""} ${file.fileid ?? ""}`.toLowerCase()
  );

  if (filenames.some((filename) => /\.(spkd|dat)(?:$|[?#])/i.test(filename))) {
    return LOG_TYPE_SPEAKER;
  }

  if (filenames.some((filename) => /\.tdms(?:$|[?#])/i.test(filename))) {
    return LOG_TYPE_TDMS;
  }

  return "";
};

type BrowserFileSystemEntry = {
  name: string;
  fullPath?: string;
  isFile: boolean;
  isDirectory: boolean;
};

type BrowserFileSystemFileEntry = BrowserFileSystemEntry & {
  file: (success: (file: File) => void, error?: (error: DOMException) => void) => void;
};

type BrowserFileSystemDirectoryEntry = BrowserFileSystemEntry & {
  createReader: () => {
    readEntries: (
      success: (entries: BrowserFileSystemEntry[]) => void,
      error?: (error: DOMException) => void
    ) => void;
  };
};

const getFileNameParts = (filename = "") => {
  const extensionMatch = filename.match(/\.([a-z0-9]{1,12})$/i);
  const extension = extensionMatch?.[1]?.toUpperCase() ?? "";
  const basename = extension
    ? filename.slice(0, -(extension.length + 1))
    : filename;

  return { basename: basename || filename, extension };
};

const getFileIcon = (extension: string) => {
  const normalizedExtension = extension.toLowerCase();

  if (normalizedExtension === "pdf") {
    return <PictureAsPdf sx={{ color: "#d74242" }} fontSize="small" />;
  }

  if (["xls", "xlsx", "csv"].includes(normalizedExtension)) {
    return <TableChartOutlined sx={{ color: "#4caf50" }} fontSize="small" />;
  }

  if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(normalizedExtension)) {
    return <ImageOutlined sx={{ color: "text.secondary" }} fontSize="small" />;
  }

  if (["mp4", "mov", "avi", "mkv", "webm"].includes(normalizedExtension)) {
    return <MovieOutlined sx={{ color: "text.secondary" }} fontSize="small" />;
  }

  if (["doc", "docx", "txt", "rtf"].includes(normalizedExtension)) {
    return <DescriptionOutlined sx={{ color: "text.secondary" }} fontSize="small" />;
  }

  return <InsertDriveFileOutlined sx={{ color: "text.secondary" }} fontSize="small" />;
};

const getFileDirectoryDeleteId = (file: FileDirectoryItem) => {
  // DELETE /files expects the object key from the filedirectory root
  // (`filedirectoryid/path/file.ext`), while KH listing may return a full
  // storage path (`harman.com/objectnode/filedirectoryid/path/file.ext`).
  const directoryId = file.filedirectoryid?.split("/").filter(Boolean).pop();

  if (directoryId) {
    const directoryMarker = `${directoryId}/`;
    const markerIndex = file.fileid.indexOf(directoryMarker);

    if (markerIndex >= 0) {
      return file.fileid.slice(markerIndex);
    }
  }

  return file.fileid;
};

const getCommonDirectoryPrefix = (paths: string[]) => {
  if (paths.length === 0) return "";

  const splitPaths = paths.map((path) => path.split("/").filter(Boolean));
  const commonSegments: string[] = [];
  const shortestLength = Math.min(...splitPaths.map((segments) => segments.length));

  for (let index = 0; index < shortestLength; index += 1) {
    const segment = splitPaths[0][index];
    if (!splitPaths.every((segments) => segments[index] === segment)) break;
    commonSegments.push(segment);
  }

  // When we do not get an explicit root directory from KH, remove only the
  // shared storage prefix and keep the first user-uploaded folder visible.
  commonSegments.pop();

  return commonSegments.join("/");
};

const getRelativeFilePath = (
  file: FileDirectoryItem,
  filedirectoryid: string | undefined,
  commonPrefix: string
) => {
  const fileid = file.fileid;
  const normalizedPath = decodeURIComponent(fileid).replace(/^\/+/, "");
  const directoryMarkers = [file.filedirectoryid, filedirectoryid]
    .filter(Boolean)
    .map((directoryId) =>
      decodeURIComponent(String(directoryId)).replace(/^\/+|\/+$/g, "")
    );

  for (const directoryMarker of directoryMarkers) {
    const markerWithSlash = `${directoryMarker}/`;
    const markerIndex = normalizedPath.indexOf(markerWithSlash);

    if (markerIndex >= 0) {
      return normalizedPath.slice(markerIndex + markerWithSlash.length);
    }
  }

  if (commonPrefix && normalizedPath.startsWith(`${commonPrefix}/`)) {
    return normalizedPath.slice(commonPrefix.length + 1);
  }

  return normalizedPath;
};

const buildFileDirectoryTree = (
  files: FileDirectoryItem[],
  filedirectoryid?: string
) => {
  const commonPrefix = getCommonDirectoryPrefix(files.map((file) => file.fileid));
  const root: FileDirectoryTreeNode[] = [];

  files.forEach((file) => {
    const relativePath = getRelativeFilePath(
      file,
      filedirectoryid,
      commonPrefix
    );
    const segments = relativePath.split("/").filter(Boolean);
    const safeSegments = segments.length > 0 ? segments : [file.filename];
    let currentLevel = root;
    let pathId = "";

    safeSegments.forEach((segment, index) => {
      const isLastSegment = index === safeSegments.length - 1;
      // The filedirectory API returns file objects, not folder objects. Only
      // intermediate path segments are folders; the final segment can also be
      // a dotfile or extensionless file such as ".DS_Store".
      const isFile = isLastSegment;
      pathId = pathId ? `${pathId}/${segment}` : segment;

      let existingNode = currentLevel.find(
        (node) => node.name === segment && node.type === (isFile ? "file" : "folder")
      );

      if (!existingNode) {
        existingNode = {
          id: pathId,
          name: segment,
          type: isFile ? "file" : "folder",
          children: [],
          file: isFile ? { ...file, filename: segment } : undefined,
        };
        currentLevel.push(existingNode);
      }

      if (!isFile) currentLevel = existingNode.children;
    });
  });

  return root;
};

const getFolderNodeIds = (nodes: FileDirectoryTreeNode[]): string[] =>
  nodes.flatMap((node) =>
    node.type === "folder"
      ? [node.id, ...getFolderNodeIds(node.children)]
      : []
  );

const getFilesInDirectoryNode = (
  node: FileDirectoryTreeNode
): FileDirectoryItem[] => {
  if (node.type === "file") return node.file ? [node.file] : [];

  return node.children.flatMap((childNode) =>
    getFilesInDirectoryNode(childNode)
  );
};

const withRelativePath = (file: File, relativePath: string): UploadableFile => {
  Object.defineProperty(file, "relativePath", {
    value: relativePath,
    configurable: true,
  });

  return file as UploadableFile;
};

const readDroppedFileEntry = (
  entry: BrowserFileSystemFileEntry,
  parentPath = ""
): Promise<UploadableFile> =>
  new Promise((resolve, reject) => {
    entry.file(
      (file) => resolve(withRelativePath(file, `${parentPath}${file.name}`)),
      reject
    );
  });

const readDroppedDirectoryEntries = (
  directoryEntry: BrowserFileSystemDirectoryEntry
): Promise<BrowserFileSystemEntry[]> =>
  new Promise((resolve, reject) => {
    const reader = directoryEntry.createReader();
    const entries: BrowserFileSystemEntry[] = [];

    const readNextBatch = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(entries);
          return;
        }

        entries.push(...batch);
        readNextBatch();
      }, reject);
    };

    readNextBatch();
  });

const readDroppedEntry = async (
  entry: BrowserFileSystemEntry,
  parentPath = ""
): Promise<UploadableFile[]> => {
  if (entry.isFile) {
    return [await readDroppedFileEntry(entry as BrowserFileSystemFileEntry, parentPath)];
  }

  if (entry.isDirectory) {
    const folderPath = `${parentPath}${entry.name}/`;
    const childEntries = await readDroppedDirectoryEntries(
      entry as BrowserFileSystemDirectoryEntry
    );
    const nestedFiles = await Promise.all(
      childEntries.map((childEntry) => readDroppedEntry(childEntry, folderPath))
    );

    return nestedFiles.flat();
  }

  return [];
};

const getFilesFromDropEvent = async (
  event: DragEvent<HTMLElement>
): Promise<UploadableFile[]> => {
  const entries = Array.from(event.dataTransfer.items ?? [])
    .map((item) => (item as any).webkitGetAsEntry?.())
    .filter(Boolean) as BrowserFileSystemEntry[];

  if (entries.length === 0) {
    return Array.from(event.dataTransfer.files) as UploadableFile[];
  }

  // Dragging folders uses the FileSystemEntry API. Traversing it preserves
  // nested paths, matching the structure users get from the Add Directory button.
  const filesByEntry = await Promise.all(
    entries.map((entry) => readDroppedEntry(entry))
  );

  return filesByEntry.flat();
};

const FileDirectoryManager = ({
  objectNodeId,
  filedirectoryid,
  files,
  loading,
  uploading,
  onUpload,
  onDelete,
}: FileDirectoryManagerProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<FileDirectoryDeleteTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedDirectoryIds, setExpandedDirectoryIds] = useState<
    Record<string, boolean>
  >({});
  const directoryInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canUpload = Boolean(objectNodeId && filedirectoryid);
  const fileTree = useMemo(
    () => buildFileDirectoryTree(files, filedirectoryid),
    [files, filedirectoryid]
  );

  useEffect(() => {
    setExpandedDirectoryIds((current) => {
      const next = { ...current };
      getFolderNodeIds(fileTree).forEach((nodeId) => {
        if (!(nodeId in next)) next[nodeId] = true;
      });
      return next;
    });
  }, [fileTree]);

  const handleFilesSelected = async (selectedFiles: File[]) => {
    if (!selectedFiles.length) return;
    await onUpload(selectedFiles);
  };

  const confirmDeleteFile = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await onDelete(deleteTarget.files);
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleDirectoryNode = (nodeId: string) => {
    setExpandedDirectoryIds((current) => ({
      ...current,
      [nodeId]: !current[nodeId],
    }));
  };

  const renderFileDirectoryNode = (
    node: FileDirectoryTreeNode,
    depth = 0
  ) => {
    const isFolder = node.type === "folder";
    const isExpanded = expandedDirectoryIds[node.id] ?? true;
    const file = node.file;
    const { basename, extension } = getFileNameParts(file?.filename ?? node.name);
    const folderFiles = isFolder ? getFilesInDirectoryNode(node) : [];

    if (isFolder) {
      return (
        <Box key={node.id}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            onClick={() => toggleDirectoryNode(node.id)}
            sx={{
              minHeight: 48,
              py: 1,
              pl: 1 + depth * 3,
              pr: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              cursor: "pointer",
              bgcolor: depth === 0 ? "rgba(0,100,153,0.02)" : "transparent",
              "&:hover": { bgcolor: "rgba(0,100,153,0.06)" },
            }}
          >
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              {isExpanded ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
              <FolderOutlined sx={{ color: "#6aa9f5" }} fontSize="small" />
              <Tooltip title={node.name}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    color: "#173f82",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {node.name}
                </Typography>
              </Tooltip>
            </Box>

            <Tooltip title="Delete folder">
              <span>
                <IconButton
                  color="error"
                  disabled={folderFiles.length === 0 || isDeleting}
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteTarget({
                      type: "folder",
                      name: node.name,
                      files: folderFiles,
                    });
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Collapse in={isExpanded}>
            <Stack spacing={0}>
              {node.children.map((childNode) =>
                renderFileDirectoryNode(childNode, depth + 1)
              )}
            </Stack>
          </Collapse>
        </Box>
      );
    }

    return (
      <Box
        key={node.id}
        sx={{
          minHeight: 54,
          py: 1,
          pl: 1 + depth * 3,
          pr: 1.5,
          maxWidth: "100%",
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: "divider",
          transition: "background-color 0.15s ease",
          "&:hover": {
            bgcolor: "rgba(0,100,153,0.06)",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          minWidth={0}
        >
          <Box display="flex" alignItems="center" gap={1} minWidth={0} flex={1}>
            {getFileIcon(extension)}
            <Box minWidth={0} flex={1}>
              <Box display="flex" alignItems="baseline" gap={1} minWidth={0}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  sx={{
                    color: "#173f82",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {basename}
                </Typography>
                {extension ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      letterSpacing: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    {extension}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>

          {/* Download is intentionally hidden for now; measurement file rows use
          delete as the primary row action.
          <Tooltip title={file?.url ? "Download" : "Download unavailable"}>
            <span>
              <IconButton
                component="a"
                href={file?.url}
                target="_blank"
                rel="noreferrer"
                disabled={!file?.url}
              >
                <Download fontSize="small" />
              </IconButton>
            </span>
          </Tooltip> */}

          <Tooltip title="Delete file">
            <span>
              <IconButton
                color="error"
                disabled={!file || isDeleting}
                onClick={() =>
                  file &&
                  setDeleteTarget({
                    type: "file",
                    name: file.filename,
                    files: [file],
                  })
                }
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
        mb={2}
      >
        <Typography variant="h6">
          <b>Logs and measurements</b>&nbsp;
          <Chip label={<b>{files.length}</b>} color="primary" size="small" />
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            disabled={!canUpload || uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Add Files
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFile />}
            disabled={!canUpload || uploading}
            onClick={() => directoryInputRef.current?.click()}
          >
            Add Directory
          </Button>
        </Stack>
      </Box>

      {!canUpload ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No file directory is available for this test requirement report yet.
        </Alert>
      ) : null}

      <Box
        onDrop={async (event) => {
          event.preventDefault();
          setIsDragOver(false);
          await handleFilesSelected(await getFilesFromDropEvent(event));
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        sx={{
          border: isDragOver ? "1px dashed" : "1px solid transparent",
          borderColor: isDragOver ? "primary.main" : "transparent",
          borderRadius: 2,
          p: files.length === 0 ? 3 : 1,
          bgcolor: isDragOver ? "rgba(0,100,153,0.04)" : "transparent",
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : files.length === 0 ? (
          <Box textAlign="center" py={3}>
            <Typography variant="h6">
              You have not uploaded any measurement files yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Upload individual files or choose a complete directory.
            </Typography>
          </Box>
        ) : (
          <Stack
            spacing={0}
            sx={{
              overflow: "hidden",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {fileTree.map((node) => renderFileDirectoryNode(node))}
          </Stack>
        )}
      </Box>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete measurement {deleteTarget?.type === "folder" ? "folder" : "file"}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will delete{" "}
            <b>{deleteTarget?.name ?? "this item"}</b>
            {deleteTarget?.type === "folder"
              ? ` and all ${deleteTarget.files.length} file(s) inside it`
              : ""}
            . Please confirm before continuing.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            disabled={isDeleting}
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isDeleting}
            onClick={confirmDeleteFile}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={async (event) => {
          await handleFilesSelected(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
      <input
        type="file"
        multiple
        hidden
        // Directory uploads are required for measurement folders. React does not
        // type the non-standard browser attributes, so set them after render.
        ref={(input) => {
          directoryInputRef.current = input;
          if (input) {
            input.setAttribute("webkitdirectory", "");
            input.setAttribute("directory", "");
          }
        }}
        onChange={async (event) => {
          await handleFilesSelected(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />

      {uploading ? (
        <Box display="flex" alignItems="center" gap={1.5} mt={2}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Uploading measurement files...
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

const TestRequirementReport = ({
  requirement,
  selectedId,
  resetTree,
  setSelectedId,
  reportnodeid,
  viewMode = "results",
}: RequirementProps) => {
  const {
    getTestReportByNodeId,
    uploadAttachments,
    loading,
    isUploading,
    setIsUploading,
    currentReport,
  } = useBoundStore();

  const [originalAttachments, setOriginalAttachments] = useState<Attachment[]>(
    []
  );
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [directoryFiles, setDirectoryFiles] = useState<FileDirectoryItem[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryUploading, setDirectoryUploading] = useState(false);
  const [isLoadingLogParameters, setIsLoadingLogParameters] = useState(false);

  const [targetReport, setTargetReport] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { dirtyFields, isDirty,errors },
    reset,
    watch,
    control,
    setValue,
  } = useForm<RequirementReportData>({
    defaultValues: {},
  });

  const selectedLogType = watch("logtype") ?? "";
  const availableLogParameters = toStringArray(watch("availablelogparameters"));
  const selectedLogParameters = toStringArray(watch("selectedlogparameters"));
  const logParameterOptions =
    selectedLogType === LOG_TYPE_TDMS
      ? Array.from(
          new Set([...availableLogParameters, ...selectedLogParameters])
        )
      : SPEAKER_LOG_PARAMETERS;

  let isEditMode = selectedId !== null;

  const onSubmit = async (data: RequirementReportData) => {
    let testRequirementIdentifier = uuidv4();

    // ✅ build dirty patch from RHF dirtyFields
    const dirtyPatch: Partial<RequirementReportData> = {};
    for (const key in dirtyFields) {
      if ((dirtyFields as any)[key]) {
        // skip attachments
        if (key === "hasattachment") continue;

        (dirtyPatch as any)[key] = (data as any)[key];
      }
    }

    // ✅ handle dates only if dirty
    if (dirtyPatch.startdate) {
      dirtyPatch.startdate =
        moment(dirtyPatch.startdate, "YYYY-MM-DD").unix() * 1000;
    }
    if (dirtyPatch.enddate) {
      dirtyPatch.enddate =
        moment(dirtyPatch.enddate, "YYYY-MM-DD").unix() * 1000;
    }

    const payload: any = {
      lang: "en",
      nodeid: targetReport?.nodeid ?? "",
      identifier: testRequirementIdentifier,
      ...dirtyPatch,
    };

    if (data.logtype === LOG_TYPE_SPEAKER) {
      delete payload.availablelogparameters;
    }

    const { toAdd, toDelete, toEdit } = getAttachmentChanges(
      originalAttachments,
      attachments
    );

    if (isDirty) {
      await updateTestRequirementReportAPI(payload);
    }
    if (toAdd.length > 0) {
      setIsUploading(true);
      await uploadAttachments(payload.nodeid, [...toAdd], []);
      setIsUploading(false);
    }
    if (toDelete.length > 0) {
      setIsUploading(true);
      await deleteAttachmentsAPI(toDelete.map((item: any) => item.nodeid));
      setIsUploading(false);
    }
    if (toEdit.length > 0) {
      setIsUploading(true);
      await uploadAttachments(payload.nodeid, [], [...toEdit]);
      setIsUploading(false);
    }

    reset({
      startdate: "",
      enddate: "",
      usedequipment: "",
      responsibletestengineer: "",
      deviationfromthemethod: "",
      chamberlogsample: "",
      functionaltestresult: "",
      observations: "",
      hasattachment: [],
      logtype: "",
      availablelogparameters: [],
      selectedlogparameters: [],
    });
    setAttachments([]);
    setOriginalAttachments([]);
    setDirectoryFiles([]);
    setSelectedId(null);

    await getTestReportByNodeId(reportnodeid);

    setTimeout(() => {
      setSelectedId(requirement.nodeid);
    }, 1000);
  };

  const normalizeApiAttachments = (apiData: any[]): Attachment[] => {
    return apiData.map((item) => ({
      id: item.attachment.file.fileid,
      title: item.attachment.title,
      description: item.attachment.description,
      url: item.attachment.file.url,
      nodeid: item.attachment.nodeid,
      mimetype: item.attachment.file.mimetype,
    }));
  };

  useEffect(() => {
    const hydrateReport = (reportData: any, reportName: string) => {
      const values: RequirementReportData = {
        nodeid: reportData?.nodeid ?? "",
        name: reportName,
        startdate: reportData?.startdate
          ? moment(reportData.startdate).format("YYYY-MM-DD")
          : "",
        enddate: reportData?.enddate
          ? moment(reportData.enddate).format("YYYY-MM-DD")
          : "",
        usedequipment: reportData?.usedequipment ?? "",
        responsibletestengineer: reportData?.responsibletestengineer ?? "",
        deviationfromthemethod: reportData?.deviationfromthemethod ?? "",
        chamberlogsample: reportData?.chamberlogsample ?? "",
        functionaltestresult: reportData?.functionaltestresult ?? "",
        observations: reportData?.observations ?? "",
        hasattachment: reportData?.hasattachment ?? [],
        logtype: reportData?.logtype ?? "",
        availablelogparameters: toStringArray(reportData?.availablelogparameters),
        selectedlogparameters: toStringArray(reportData?.selectedlogparameters),
        testaccordingtoaccreditation:
          reportData?.testaccordingtoaccreditation || false,
      };

      reset(values, { keepDefaultValues: false, keepDirty: false });

      const normalized = normalizeApiAttachments(reportData?.hasattachment || []);
      setOriginalAttachments(normalized);
      setAttachments(normalized);
      setTargetReport(reportData);
    };

    if (requirement?.testrequirementreport) {
      hydrateReport(requirement.testrequirementreport, requirement?.name ?? "");
      return;
    }

    let cReport: any = structuredClone(currentReport);
    cReport?.testreport?.hastestrequirementreport &&
      cReport?.testreport?.hastestrequirementreport.map((item: any) => {
        if (
          item.testrequirementreport.istestrequirementreportof[0]
            .testrequirement.nodeid === requirement?.nodeid
        ) {
          const values: RequirementReportData = {
            nodeid: item.testrequirementreport?.nodeid ?? "",
            name: requirement?.name ?? "",
            startdate: item.testrequirementreport?.startdate
              ? moment(item.testrequirementreport.startdate).format(
                  "YYYY-MM-DD"
                )
              : "",
            enddate: item.testrequirementreport?.enddate
              ? moment(item.testrequirementreport.enddate).format("YYYY-MM-DD")
              : "",
            usedequipment: item.testrequirementreport?.usedequipment ?? "",
            responsibletestengineer:
              item.testrequirementreport?.responsibletestengineer ?? "",
            deviationfromthemethod:
              item.testrequirementreport?.deviationfromthemethod ?? "",
            chamberlogsample:
              item.testrequirementreport?.chamberlogsample ?? "",
            functionaltestresult:
              item.testrequirementreport?.functionaltestresult ?? "",
            observations: item.testrequirementreport?.observations ?? "",
            hasattachment: item.testrequirementreport?.hasattachment ?? [],
            logtype: item.testrequirementreport?.logtype ?? "",
            availablelogparameters: toStringArray(
              item.testrequirementreport?.availablelogparameters
            ),
            selectedlogparameters: toStringArray(
              item.testrequirementreport?.selectedlogparameters
            ),
            testaccordingtoaccreditation: item.testrequirementreport?.testaccordingtoaccreditation || false,
          };

          reset(values, { keepDefaultValues: false, keepDirty: false });

          const normalized = normalizeApiAttachments(
            item.testrequirementreport?.hasattachment || []
          );
          setOriginalAttachments(normalized);
          setAttachments(normalized);

          setTargetReport(item.testrequirementreport);
        }
      });
  }, [requirement]);

  const filedirectoryid = getMeasurementFileDirectoryId(targetReport);
  const fileDirectoryObjectNodeId = targetReport?.nodeid ?? "";

  const loadDirectoryFiles = useCallback(async () => {
    if (!fileDirectoryObjectNodeId || !filedirectoryid) {
      setDirectoryFiles([]);
      return;
    }

    setDirectoryLoading(true);
    try {
      const files = await getFileDirectoryFilesAPI(
        fileDirectoryObjectNodeId,
        filedirectoryid
      );
      setDirectoryFiles(files);
    } catch (error) {
      console.error("Failed to load measurement files", error);
      toast.error("Failed to load measurement files");
    } finally {
      setDirectoryLoading(false);
    }
  }, [fileDirectoryObjectNodeId, filedirectoryid]);

  useEffect(() => {
    // Measurement files are no longer attachments. They live in a KH
    // filedirectory, so selecting a report must read the directory contents.
    loadDirectoryFiles();
  }, [loadDirectoryFiles]);

  useEffect(() => {
    if (selectedLogType || directoryFiles.length === 0) return;

    const detectedLogType = detectLogTypeFromFiles(directoryFiles);
    if (!detectedLogType) return;

    setValue("logtype", detectedLogType, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (
      detectedLogType === LOG_TYPE_SPEAKER &&
      selectedLogParameters.length === 0
    ) {
      setValue("selectedlogparameters", DEFAULT_SPEAKER_LOG_PARAMETERS, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [directoryFiles, selectedLogParameters.length, selectedLogType, setValue]);

  const handleLogTypeChange = (nextLogType: string) => {
    setValue("logtype", nextLogType, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (nextLogType === LOG_TYPE_SPEAKER) {
      setValue("selectedlogparameters", DEFAULT_SPEAKER_LOG_PARAMETERS, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    setValue("selectedlogparameters", [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleFetchTdmsLogParameters = async () => {
    if (!targetReport?.nodeid) return;

    setIsLoadingLogParameters(true);
    try {
      const response = await getTdmsLogParametersAPI(targetReport.nodeid);
      const parameters = normalizeTdmsParameters(response);

      setValue("availablelogparameters", parameters, {
        shouldDirty: true,
        shouldValidate: true,
      });

      // Keep already selected TDMS parameters only if the backend still returns
      // them. This prevents stale values from being saved after a refetch.
      setValue(
        "selectedlogparameters",
        selectedLogParameters.filter((parameter) =>
          parameters.includes(parameter)
        ),
        { shouldDirty: true, shouldValidate: true }
      );

      if (parameters.length === 0) {
        toast.info("No TDMS log parameters returned.");
      } else {
        toast.success("TDMS log parameters loaded.");
      }
    } catch (error) {
      console.error("Failed to get TDMS log parameters", error);
      toast.error("Failed to get TDMS log parameters");
    } finally {
      setIsLoadingLogParameters(false);
    }
  };

  const handleDirectoryUpload = useCallback(async (files: File[]) => {
    if (!fileDirectoryObjectNodeId || !filedirectoryid) return;

    setDirectoryUploading(true);
    try {
      const uploadUrls = await requestFileDirectoryUploadUrlsAPI(
        files.map((file) => ({
          nodeid: fileDirectoryObjectNodeId,
          filedirectoryid,
          filename:
            (file as UploadableFile).relativePath ||
            file.webkitRelativePath ||
            file.name,
        }))
      );

      await uploadFilesToFileDirectory(uploadUrls, files);
      await loadDirectoryFiles();
      toast.success("Measurement files uploaded.");
    } catch (error) {
      console.error("Failed to upload measurement files", error);
      toast.error("Failed to upload measurement files");
    } finally {
      setDirectoryUploading(false);
    }
  }, [fileDirectoryObjectNodeId, filedirectoryid, loadDirectoryFiles]);

  const handleDirectoryFileDelete = useCallback(async (files: FileDirectoryItem[]) => {
    if (!fileDirectoryObjectNodeId || files.length === 0) return;

    try {
      await deleteFilesAPI(
        files.map((file) => ({
          nodeid: fileDirectoryObjectNodeId,
          fileid: getFileDirectoryDeleteId(file),
        }))
      );
      await loadDirectoryFiles();
      toast.success(
        files.length === 1
          ? "Measurement file deleted."
          : "Measurement folder deleted."
      );
    } catch (error) {
      console.error("Failed to delete measurement file(s)", error);
      toast.error("Failed to delete measurement file(s)");
      throw error;
    }
  }, [fileDirectoryObjectNodeId, loadDirectoryFiles]);

  const attachmentsChanged = useMemo(() => {
    return JSON.stringify(originalAttachments) !== JSON.stringify(attachments);
  }, [attachments, originalAttachments]);

  const pictureAttachments = useMemo(
    () => attachments.filter((attachment) => isImageAttachment(attachment)),
    [attachments]
  );

  const syncAttachmentSection = (
    updatedSection: Attachment[],
    isTargetAttachment: (attachment: Attachment) => boolean
  ) => {
    const untouched = attachments.filter((attachment) => !isTargetAttachment(attachment));
    setAttachments([...untouched, ...updatedSection]);
  };

  return (
    <>
      <Backdrop open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <form key={selectedId} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {viewMode === "results" ? (
            <>
              <Grid size={12}>
                <Typography variant="subtitle1">
                  Name: <b>{watch("name")}</b>
                </Typography>
              </Grid>

              <Grid size={6}>
                <TextField
                  type="date"
                  label="Start Date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...register("startdate", {
                    validate: (value) => {
                      const enddate = watch("enddate");
                      if (
                        value &&
                        enddate &&
                        moment(value).isAfter(moment(enddate))
                      ) {
                        return "Start date cannot be after end date";
                      }
                      return true;
                    },
                  })}
                  error={!!errors.startdate}
                  helperText={errors.startdate?.message}
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  type="date"
                  label="End Date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...register("enddate", {
                    validate: (value) => {
                      const startdate = watch("startdate");
                      if (
                        value &&
                        startdate &&
                        moment(value).isBefore(moment(startdate))
                      ) {
                        return "End date cannot be before start date";
                      }
                      return true;
                    },
                  })}
                  error={!!errors.enddate}
                  helperText={errors.enddate?.message}
                />
              </Grid>

              {/* <Grid size={12}>
                <TextField
                  label="Used equipment"
                  fullWidth
                  {...register("usedequipment")}
                />
              </Grid> */}

              <Grid size={12}>
                <TextField
                  label="Responsible test engineer"
                  fullWidth
                  {...register("responsibletestengineer")}
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Deviation from the method"
                  fullWidth
                  multiline
                  rows={2}
                  {...register("deviationfromthemethod")}
                />
              </Grid>

              {/* Hidden per request — keep field/data intact, just not editable here.
              <Grid size={12}>
                <TextField
                  label="Chamber log / Test parameters"
                  fullWidth
                  multiline
                  rows={2}
                  {...register("chamberlogsample")}
                />
              </Grid>
              */}

              <Grid size={12}>
                <TextField
                  label="Functional test result"
                  fullWidth
                  multiline
                  rows={2}
                  {...register("functionaltestresult")}
                />
              </Grid>

              {/* Hidden per request — keep field/data intact, just not editable here.
              <Grid size={12}>
                <TextField
                  label="Observations"
                  fullWidth
                  multiline
                  rows={2}
                  {...register("observations")}
                />
              </Grid>

              <Grid size={6}>
                <Controller
                  control={control}
                  name="testaccordingtoaccreditation"
                  defaultValue={false}
                  render={({ field }) => (
                    <Checkbox
                      label="Test according to accreditation"
                      checked={field.value || false}
                      onChange={field.onChange}
                      value={watch("testaccordingtoaccreditation") || false}
                    />
                  )}
                />
              </Grid>
              */}
            </>
          ) : (
            <>
              <Grid size={12}>
                <AttachmentManager
                  title="Pictures"
                  _attachments={pictureAttachments}
                  onChange={(updated) =>
                    syncAttachmentSection(updated, isImageAttachment)
                  }
                  defaultExpanded
                  allowedFileExtensions={[...IMAGE_EXTS]}
                  cropFeature={false}
                  previewImages
                  showImageThumbs
                />
              </Grid>

              <Grid size={12}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: "rgba(0, 104, 153, 0.02)",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Log Settings
                  </Typography>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", md: "flex-start" }}
                    sx={{ maxWidth: 800 }}
                  >
                    <FormControl fullWidth sx={{ minWidth: 0 }}>
                      <InputLabel id="log-type-label">Log Type</InputLabel>
                      <Controller
                        name="logtype"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="log-type-label"
                            label="Log Type"
                            value={field.value ?? ""}
                            onChange={(event) =>
                              handleLogTypeChange(String(event.target.value))
                            }
                          >
                            {LOG_TYPE_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>

                    <FormControl
                      fullWidth
                      disabled={!selectedLogType}
                      sx={{ minWidth: 0 }}
                    >
                      <InputLabel id="log-parameters-label">
                        Log Parameters
                      </InputLabel>
                      <Controller
                        name="selectedlogparameters"
                        control={control}
                        defaultValue={[]}
                        render={({ field }) => (
                          <Select
                            multiple
                            labelId="log-parameters-label"
                            label="Log Parameters"
                            value={toStringArray(field.value)}
                            input={<OutlinedInput label="Log Parameters" />}
                            onChange={(event) => {
                              const value = event.target.value;
                              field.onChange(
                                typeof value === "string"
                                  ? value.split(",")
                                  : value
                              );
                            }}
                            renderValue={(selected) => {
                              const selectedValues = selected as string[];

                              return (
                                <Tooltip
                                  arrow
                                  title={
                                    selectedValues.length > 0 ? (
                                      <Box
                                        component="ul"
                                        sx={{ m: 0, pl: 2 }}
                                      >
                                        {selectedValues.map((value) => (
                                          <li key={value}>{value}</li>
                                        ))}
                                      </Box>
                                    ) : (
                                      ""
                                    )
                                  }
                                >
                                  <Box
                                    component="span"
                                    aria-label={selectedValues.join(", ")}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      minWidth: 0,
                                      gap: 0.75,
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {selectedValues[0] ?? ""}
                                    </Box>
                                    {selectedValues.length > 1 && (
                                      <Box
                                        component="span"
                                        sx={{
                                          flexShrink: 0,
                                          color: "text.secondary",
                                        }}
                                      >
                                        +{selectedValues.length - 1} others
                                      </Box>
                                    )}
                                  </Box>
                                </Tooltip>
                              );
                            }}
                          >
                            {logParameterOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                <MUICheckbox
                                  checked={selectedLogParameters.includes(
                                    option
                                  )}
                                />
                                <ListItemText primary={option} />
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>

                    <Button
                      variant="outlined"
                      onClick={handleFetchTdmsLogParameters}
                      disabled={
                        selectedLogType !== LOG_TYPE_TDMS ||
                        isLoadingLogParameters ||
                        !targetReport?.nodeid
                      }
                      sx={{
                        minWidth: 210,
                        mt: { xs: 0, md: 0.75 },
                      }}
                    >
                      {isLoadingLogParameters ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "Get Log Parameters"
                      )}
                    </Button>
                  </Stack>
                </Box>
              </Grid>

              <Grid size={12}>
                <FileDirectoryManager
                  objectNodeId={fileDirectoryObjectNodeId}
                  filedirectoryid={filedirectoryid}
                  files={directoryFiles}
                  loading={directoryLoading}
                  uploading={directoryUploading}
                  onUpload={handleDirectoryUpload}
                  onDelete={handleDirectoryFileDelete}
                />
              </Grid>
            </>
          )}

          {/* Actions */}
          <Grid size={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={
                  (isEditMode && !isDirty && !attachmentsChanged) ||
                  isUploading ||
                  directoryUploading ||
                  requirement?.nodeid === undefined
                }
              >
                {isUploading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </Grid>

         

        </Grid>
      </form>
    </>
  );
};

export default TestRequirementReport;
